/* ==========================================================================
   Oakhold — core interaction layer
   Behaviours are declared in markup via data-oh-* attributes so sections stay
   declarative and every behaviour re-binds cleanly on theme-editor reloads.
   One easing curve, transform/opacity only, all motion off under
   prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer:fine)').matches;

  /* rAF-throttled scroll bus — one listener for the whole page */
  var scrollSubs = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      for (var i = 0; i < scrollSubs.length; i++) {
        try { scrollSubs[i](); } catch (e) { /* keep the bus alive */ }
      }
      ticking = false;
    });
  }
  function subscribeScroll(fn) {
    scrollSubs.push(fn);
    fn();
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* Mark elements so a behaviour never binds twice.
     Kept off dataset on purpose: a key like 'Drawer' would map to
     data-oh-drawer, which is the very attribute being guarded, so an
     attribute with a value ("cart", "92") would read as already-claimed
     and the behaviour would never bind. */
  var claimed = new WeakMap();
  function claim(el, key) {
    var keys = claimed.get(el);
    if (!keys) { keys = {}; claimed.set(el, keys); }
    if (keys[key]) return false;
    keys[key] = true;
    return true;
  }

  /* ------------------------------------------------------------ reveal --- */
  function initReveal(root) {
    var els = root.querySelectorAll('.oh-rv');
    if (!els.length) return;
    if (reduced) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: .12, rootMargin: '0px 0px -40px 0px' });

    /* Anything already on screen is released synchronously. The observer's
       first callback is a frame away, and waiting for it left above-the-fold
       content — the whole PDP buy box — blank on first paint. */
    var vh = window.innerHeight || document.documentElement.clientHeight;
    els.forEach(function (el) {
      if (!claim(el, 'Reveal')) return;
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) { el.classList.add('is-in'); return; }
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------- marquee --- */
  function initMarquee(root) {
    root.querySelectorAll('[data-oh-marquee]').forEach(function (track) {
      if (!claim(track, 'Marquee')) return;
      /* Duplicate once so the -50% keyframe loops seamlessly */
      track.innerHTML += track.innerHTML;
    });
  }

  /* ----------------------------------------------------------- count up --- */
  function initCountUp(root) {
    root.querySelectorAll('[data-oh-countup]').forEach(function (el) {
      if (!claim(el, 'Count')) return;
      /* The final value is already in the DOM from Liquid. This only ever
         animates toward it, and never writes a value it cannot finish — a
         stalled rAF used to leave the page showing "0 Brace". */
      var final = el.textContent;
      var target = parseFloat(el.getAttribute('data-oh-countup'));
      if (isNaN(target)) return;
      var decimals = (String(target).split('.')[1] || '').length;
      if (reduced) return;

      function run() {
        var t0 = null;
        function tick(ts) {
          if (t0 === null) { t0 = ts; requestAnimationFrame(tick); return; }
          var p = Math.min((ts - t0) / 900, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          if (p >= 1) { el.textContent = final; return; }
          el.textContent = (target * eased).toFixed(decimals);
          requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      }

      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) { run(); return; }
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        run();
      }, { threshold: .5 }).observe(el);
    });
  }

  /* --------------------------------------------------------- accordion --- */
  function initAccordion(root) {
    root.querySelectorAll('[data-oh-accordion]').forEach(function (group) {
      if (!claim(group, 'Accordion')) return;
      var single = group.getAttribute('data-oh-accordion') !== 'multi';
      group.querySelectorAll('[data-oh-accordion-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-oh-accordion-item]');
          var panel = item.querySelector('[data-oh-accordion-panel]');
          var open = item.classList.contains('is-open');
          if (single) {
            group.querySelectorAll('[data-oh-accordion-item]').forEach(function (i) {
              i.classList.remove('is-open');
              var t = i.querySelector('[data-oh-accordion-trigger]');
              var p = i.querySelector('[data-oh-accordion-panel]');
              if (t) t.setAttribute('aria-expanded', 'false');
              if (p) p.style.maxHeight = '';
            });
          }
          if (!open) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
          } else {
            item.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
            if (panel) panel.style.maxHeight = '';
          }
        });
      });
      /* Size any item that ships open */
      group.querySelectorAll('[data-oh-accordion-item].is-open').forEach(function (i) {
        var p = i.querySelector('[data-oh-accordion-panel]');
        var t = i.querySelector('[data-oh-accordion-trigger]');
        if (t) t.setAttribute('aria-expanded', 'true');
        if (p) p.style.maxHeight = p.scrollHeight + 'px';
      });
    });
  }

  /* -------------------------------------------------------------- rail --- */
  function initRail(root) {
    root.querySelectorAll('[data-oh-rail]').forEach(function (rail) {
      if (!claim(rail, 'Rail')) return;
      var scope = rail.closest('[data-oh-rail-scope]') || root;
      var prev = scope.querySelector('[data-oh-rail-prev]');
      var next = scope.querySelector('[data-oh-rail-next]');
      function step() {
        var card = rail.firstElementChild;
        if (!card) return 320;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 22) || 22;
        return card.getBoundingClientRect().width + gap;
      }
      function go(dir) {
        rail.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
      }
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;
      }
      rail.addEventListener('scroll', function () {
        if (rail.dataset.wlRailTick) return;
        rail.dataset.wlRailTick = '1';
        requestAnimationFrame(function () { sync(); delete rail.dataset.wlRailTick; });
      }, { passive: true });
      sync();
    });
  }

  /* ------------------------------------------------------ sticky steps --- */
  function initSteps(root) {
    var lists = root.querySelectorAll('.oh-how__steps');
    var steps = root.querySelectorAll('[data-oh-step]');
    if (!steps.length) return;

    /* Show the image belonging to a step index, if the section has one. */
    function showShot(section, i) {
      var well = section.querySelector('[data-oh-step-media]');
      if (!well) return;
      var match = well.querySelector('[data-oh-step-shot="' + i + '"]');
      /* No picture on this step: hold whatever is showing rather than
         flashing back to the base image mid-scroll. */
      if (!match) return;
      well.querySelectorAll('[data-oh-step-shot]').forEach(function (el) {
        el.classList.toggle('is-on', el === match);
      });
    }

    lists.forEach(function (list) {
      if (!claim(list, 'StepLine')) return;

      var section = list.closest('.oh-how') || document;
      var items = list.querySelectorAll('[data-oh-step]');
      if (!items.length) return;

      /* Dot centre, measured relative to the list. Read from layout rather than
         from the ::before box: getComputedStyle reports the 11px content height
         and misses the 2px border either side, and offsetTop is relative to the
         li (which is positioned), not to the list. Both errors shifted the line
         off the dots. */
      function dotY(el) {
        var cs = getComputedStyle(el, '::before');
        var top = parseFloat(cs.top) || 0;
        var h = parseFloat(cs.height) || 0;
        var bt = parseFloat(cs.borderTopWidth) || 0;
        var bb = parseFloat(cs.borderBottomWidth) || 0;
        var liTop = el.getBoundingClientRect().top - list.getBoundingClientRect().top;
        return liTop + top + (h + bt + bb) / 2;
      }

      function update() {
        var first = dotY(items[0]);
        var last = dotY(items[items.length - 1]);
        /* Progress is measured against a line 55% down the viewport: the point
           where a step reads as "the one being looked at". */
        var mark = window.innerHeight * 0.55;
        var y = mark - list.getBoundingClientRect().top;

        list.style.setProperty('--oh-how-start', first + 'px');
        var fill = Math.max(0, Math.min(y, last)) - first;
        list.style.setProperty('--oh-how-progress', Math.max(0, fill) + 'px');

        /* Light every dot the line has reached, and show the last one's image. */
        var current = -1;
        items.forEach(function (el, i) {
          if (y >= dotY(el)) current = i;
        });
        /* Below the first dot nothing has been reached yet; show step one so the
           pane is never blank on a stacked layout. */
        if (current < 0) current = 0;

        /* Every step the line has passed stays lit, which reads as a
           timeline. Stacked and full width behave the same: the mobile list
           scrolls in normal flow, so nothing prints over anything else. */
        items.forEach(function (el, i) {
          el.classList.toggle('is-active', i <= current);
        });

        showShot(section, current);
      }

      subscribeScroll(update);
      update();
      /* Late-loading images change the offsets the maths depends on. */
      window.addEventListener('load', update);
    });
  }

  /* ------------------------------------------------------- word reveal --- */
  function initWords(root) {
    root.querySelectorAll('[data-oh-words]').forEach(function (el) {
      if (!claim(el, 'Words')) return;
      if (reduced) { el.classList.add('is-words-in'); return; }
      requestAnimationFrame(function () {
        setTimeout(function () { el.classList.add('is-words-in'); }, 120);
      });
    });
  }

  /* --------------------------------------------------------- ugc video --- */
  /* Plays each clip only while it is on screen: a rail of five autoplaying
     videos would compete for bandwidth and keep decoding off-screen frames.
     Controls appear once a clip is genuinely playing, so a still wall stays
     clean, and stay put afterwards so a visitor who paused can resume. */
  function initUgcVideo(root) {
    root.querySelectorAll('[data-oh-ugcvideo]').forEach(function (scope) {
      if (!claim(scope, 'UgcVideo')) return;

      var vids = scope.querySelectorAll('video');
      if (!vids.length) return;

      vids.forEach(function (v) {
        /* muted is what makes autoplay permissible; set it on the element as
           well as the attribute because some browsers ignore the attribute
           alone once the element has been adopted. */
        v.muted = true;
        v.playsInline = true;

        function showControls() {
          if (!v.controls) v.controls = true;
        }
        v.addEventListener('play', showControls);
        /* A visitor who taps the clip gets controls even if play() was blocked. */
        v.addEventListener('click', showControls);
      });

      /* Reduced motion: never start anything by itself, just expose controls. */
      if (reduced || !('IntersectionObserver' in window)) {
        vids.forEach(function (v) { v.controls = true; });
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            var p = v.play();
            /* Autoplay can still be refused; surface controls so the clip is
               not stranded with no way to start it. */
            if (p && p.catch) { p.catch(function () { v.controls = true; }); }
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: 0.5 });

      vids.forEach(function (v) { io.observe(v); });
    });
  }

  /* ---------------------------------------------------------- magnetic --- */
  function initMagnetic(root) {
    if (!finePointer || reduced) return;
    root.querySelectorAll('[data-oh-magnetic]').forEach(function (btn) {
      if (!claim(btn, 'Magnetic')) return;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        var dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        btn.style.transform = 'translate(' + (dx * 5).toFixed(1) + 'px,' + (dy * 4 - 2).toFixed(1) + 'px)';
      });
      btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
    });
  }

  /* ---------------------------------------------------------- parallax --- */
  function initParallax(root) {
    if (!finePointer || reduced) return;
    root.querySelectorAll('[data-oh-parallax-scope]').forEach(function (scope) {
      if (!claim(scope, 'Parallax')) return;
      var layers = scope.querySelectorAll('[data-oh-parallax-depth]');
      if (!layers.length) return;
      scope.addEventListener('mousemove', function (e) {
        var r = scope.getBoundingClientRect();
        var nx = (e.clientX - r.left) / r.width - .5;
        var ny = (e.clientY - r.top) / r.height - .5;
        layers.forEach(function (l) {
          var d = parseFloat(l.getAttribute('data-oh-parallax-depth')) || 0;
          l.style.setProperty('--oh-px', (nx * d).toFixed(2) + 'px');
          l.style.setProperty('--oh-py', (ny * d * .8).toFixed(2) + 'px');
        });
      });
      scope.addEventListener('mouseleave', function () {
        layers.forEach(function (l) {
          l.style.setProperty('--oh-px', '0px');
          l.style.setProperty('--oh-py', '0px');
        });
      });
    });
  }

  /* --------------------------------------------------------- nav + bar --- */
  function initNav(root) {
    var nav = root.querySelector('[data-oh-nav]');
    if (nav && claim(nav, 'Nav')) {
      var progress = nav.querySelector('[data-oh-progress]');
      subscribeScroll(function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        nav.classList.toggle('is-scrolled', window.scrollY > 10);
        if (progress) progress.style.width = (max > 0 ? (window.scrollY / max) * 100 : 0) + '%';
      });
    }

    var bar = document.querySelector('[data-oh-buybar]');
    if (bar && claim(bar, 'Buybar')) {
      subscribeScroll(function () {
        var after = document.querySelector('[data-oh-buybar-after]');
        var until = document.querySelector('[data-oh-buybar-until]');
        var suppress = document.querySelector('[data-oh-buybar-suppress]');
        var past = after ? window.scrollY > after.offsetTop + after.offsetHeight - 40 : window.scrollY > 500;
        var atEnd = until ? until.getBoundingClientRect().top < window.innerHeight : false;
        var inQuiet = false;
        if (suppress) {
          var r = suppress.getBoundingClientRect();
          inQuiet = r.top < window.innerHeight - 80 && r.bottom > 80;
        }
        bar.classList.toggle('is-show', past && !atEnd && !inQuiet);
      });
    }
  }

  /* ------------------------------------------------------- canal lab --- */
  /* Scroll-scrubbed comparison: the unsupported foot drops while the braced
     foot holds level and the cable draws in. */
  /* ----------------------------------------------------------------- lab --- */
  /* Gait Lab. A sticky section scrubs a single progress value 0..1; the left
     lane rotates the foot down to 26 degrees, the right lane draws the amber
     cable in by dash-offset. Only a transform and a dash-offset change, so the
     whole thing animates on the compositor. */
  function initLab(root) {
    root.querySelectorAll('[data-oh-lab]').forEach(function (track) {
      if (!claim(track, 'Lab')) return;
      var q = function (sel) { return track.querySelector(sel); };
      var push = q('[data-oh-lab-push]'), lift = q('[data-oh-lab-lift]');
      var foot = q('[data-oh-lab-foot]'), cable = q('[data-oh-lab-cable]');
      var bar = q('[data-oh-lab-bar]'), hint = q('[data-oh-lab-hint]');
      var S = (window.Oakhold && window.Oakhold.strings) || {};

      function render(p) {
        /* Headline crossfade: the toe drops, then the cable holds. */
        if (push) {
          push.style.opacity = p < .55 ? 1 - Math.max(0, (p - .4)) / .15 : 0;
          push.style.filter = 'blur(' + (p > .4 ? Math.min(8, (p - .4) * 60) : 0) + 'px)';
        }
        if (lift) {
          lift.style.opacity = p > .5 ? Math.min(1, (p - .5) / .15) : 0;
          lift.style.filter = 'blur(' + (p < .65 ? Math.max(0, 8 - (p - .5) * 60) : 0) + 'px)';
        }

        if (foot) foot.style.setProperty('--oh-drop', (p * 26) + 'deg');
        /* The cable finishes drawing before the scrub ends, so the "held"
           state is legible rather than completing on the last pixel. */
        if (cable) cable.style.setProperty('--oh-p', Math.min(1, p * 1.6));

        track.classList.toggle('is-s2', p > .45);
        track.classList.toggle('is-s3', p > .7);

        if (bar) bar.style.width = (p * 100) + '%';
        if (hint) {
          hint.textContent = p < .05 ? (S.labStart || 'Scroll to run the comparison')
            : (p > .95 ? (S.labDone || "That's the whole difference") : (S.labScrolling || 'Keep scrolling'));
        }
      }

      if (reduced) { render(1); return; }
      subscribeScroll(function () {
        var span = track.offsetHeight - window.innerHeight;
        var p = span > 0 ? (window.scrollY - track.offsetTop) / span : 0;
        render(Math.max(0, Math.min(1, p)));
      });
    });
  }

  /* ------------------------------------------------------------- drawer --- */
  function initDrawer(root) {
    root.querySelectorAll('[data-oh-drawer]').forEach(function (drawer) {
      if (!claim(drawer, 'Drawer')) return;
      var id = drawer.getAttribute('data-oh-drawer');
      var lastFocus = null;

      function setExpanded(state) {
        document.querySelectorAll('[data-oh-drawer-open="' + id + '"]').forEach(function (b) {
          if (b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', state);
        });
      }
      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        setExpanded('true');
        var focusable = drawer.querySelector('[data-oh-drawer-close]');
        if (focusable) focusable.focus();
        document.addEventListener('keydown', onKey);
      }
      function close() {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        setExpanded('false');
        document.removeEventListener('keydown', onKey);
        setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.setAttribute('hidden', ''); }, 500);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      function onKey(e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        var items = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }

      drawer.wlOpen = open;
      drawer.wlClose = close;
      drawer.querySelectorAll('[data-oh-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });

      document.querySelectorAll('[data-oh-drawer-open="' + id + '"]').forEach(function (b) {
        if (!claim(b, 'DrawerOpen')) return;
        b.addEventListener('click', function (e) { e.preventDefault(); open(); });
      });
    });
  }

  /* --------------------------------------------------------------- money --- */
  /* Shopify money formats are Liquid templates; mirror them client-side so
     prices swapped by the pack selector match server-rendered prices exactly. */
  function formatMoney(cents) {
    var fmt = window.Oakhold && window.Oakhold.moneyFormat;
    if (!fmt) return '$' + (cents / 100).toFixed(2);
    function group(decimals, thousands, decimal) {
      var parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? parts.join(decimal) : parts[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return group(2, ',', '.');
        case 'amount_no_decimals': return group(0, ',', '.');
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        case 'amount_with_space_separator': return group(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(2, "'", '.');
        default: return group(2, ',', '.');
      }
    });
  }

  /* -------------------------------------------------------------- switch --- */
  /* One generic switcher drives the hero media chips and the use-case tabs:
     a button carries data-key, panels with the same data-key become active,
     and any data-swap-* attributes on the button are written into targets. */
  function initSwitch(root) {
    root.querySelectorAll('[data-oh-switch]').forEach(function (group) {
      if (!claim(group, 'Switch')) return;
      var buttons = group.querySelectorAll('[data-oh-switch-btn]');
      var panels = group.querySelectorAll('[data-oh-switch-panel]');

      function activate(key, btn) {
        buttons.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          if (b.hasAttribute('role') && b.getAttribute('role') === 'tab') b.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-on', p.getAttribute('data-oh-switch-panel') === key);
        });
        /* Copy swaps: data-swap-<targetName> on the button -> [data-oh-swap="<targetName>"] */
        Array.prototype.slice.call(btn.attributes).forEach(function (attr) {
          if (attr.name.indexOf('data-swap-') !== 0) return;
          var target = group.querySelector('[data-oh-swap="' + attr.name.slice(10) + '"]');
          if (target) target.innerHTML = attr.value;
        });
        group.setAttribute('data-oh-switch-active', key);
      }

      buttons.forEach(function (b) {
        b.addEventListener('click', function () { activate(b.getAttribute('data-oh-switch-btn'), b); });
        b.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var list = Array.prototype.slice.call(buttons);
          var i = list.indexOf(b);
          var n = list[(i + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
          n.focus();
          activate(n.getAttribute('data-oh-switch-btn'), n);
        });
      });
    });
  }

  /* ------------------------------------------------------------ hotspot --- */
  function initHotspots(root) {
    root.querySelectorAll('[data-oh-hotspot-scope]').forEach(function (scope) {
      if (!claim(scope, 'Hotspot')) return;
      var cards = scope.querySelectorAll('[data-oh-hotspot-card]');
      function closeAll() {
        cards.forEach(function (c) { c.classList.remove('is-on'); });
        scope.querySelectorAll('[data-oh-hotspot]').forEach(function (h) { h.setAttribute('aria-expanded', 'false'); });
      }
      scope.querySelectorAll('[data-oh-hotspot]').forEach(function (h) {
        h.addEventListener('click', function (e) {
          e.stopPropagation();
          var id = h.getAttribute('data-oh-hotspot');
          var card = scope.querySelector('[data-oh-hotspot-card="' + id + '"]');
          var wasOpen = card && card.classList.contains('is-on');
          closeAll();
          if (card && !wasOpen) { card.classList.add('is-on'); h.setAttribute('aria-expanded', 'true'); }
        });
      });
      scope.addEventListener('click', function (e) {
        if (!e.target.closest('[data-oh-hotspot]') && !e.target.closest('[data-oh-hotspot-card]')) closeAll();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
    });
  }


  /* ------------------------------------------------------------- gallery --- */
  /* Standard product media gallery: a stack of absolutely-positioned slides
     inside an aspect-ratio box, driven by a thumbnail tablist. The box owns the
     height, so switching media can never shift the page. */
  function initGallery(root) {
    root.querySelectorAll('[data-oh-gallery]').forEach(function (gal) {
      if (!claim(gal, 'Gallery')) return;

      var slides = gal.querySelectorAll('[data-oh-gallery-slide]');
      var thumbs = gal.querySelectorAll('[data-oh-gallery-thumb]');
      if (!slides.length) return;

      /* Index lookup so a swap knows which way to travel. */
      function indexOf(id) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-oh-gallery-slide') === id) return i;
        }
        return -1;
      }

      var current = indexOf(
        (gal.querySelector('[data-oh-gallery-slide].is-active') || slides[0])
          .getAttribute('data-oh-gallery-slide')
      );

      function show(id) {
        var next = indexOf(id);
        if (next < 0 || next === current) return;

        /* Moving forward pushes the new slide in from the right, back from the
           left. Set before the class flip so the browser has a start position
           to animate from. */
        var forward = next > current;
        slides.forEach(function (s) {
          s.classList.toggle('is-next', forward);
          s.classList.toggle('is-prev', !forward);
        });
        current = next;

        slides.forEach(function (s) {
          var on = s.getAttribute('data-oh-gallery-slide') === id;
          s.classList.toggle('is-active', on);
          if (on) { s.removeAttribute('aria-hidden'); }
          else { s.setAttribute('aria-hidden', 'true'); }
          /* Pause any video that scrolls out of view of the eye. */
          if (!on) {
            var v = s.querySelector('video');
            if (v && !v.paused) { try { v.pause(); } catch (e) {} }
          }
        });
        thumbs.forEach(function (t) {
          var on = t.getAttribute('data-oh-gallery-thumb') === id;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (on && t.parentElement) {
            var rail = t.parentElement;
            var railRect = rail.getBoundingClientRect();
            var thumbRect = t.getBoundingClientRect();
            var delta = thumbRect.left - railRect.left - (rail.clientWidth - thumbRect.width) / 2;
            rail.scrollTo({
              left: Math.max(0, rail.scrollLeft + delta),
              behavior: reduced ? 'auto' : 'smooth'
            });
          }
        });
      }

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          show(t.getAttribute('data-oh-gallery-thumb'));
        });
        t.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          var next = e.key === 'ArrowRight' ? i + 1 : i - 1;
          if (next < 0) next = thumbs.length - 1;
          if (next >= thumbs.length) next = 0;
          var target = thumbs[next];
          show(target.getAttribute('data-oh-gallery-thumb'));
          target.focus();
        });
      });

      /* Finger swipe on the main photo. The slide tracks the finger, then eases
         the rest of the way. A short flick counts. Vertical movement still scrolls
         the page. */
      var stage = gal.querySelector('.oh-gallery__main');
      if (stage && slides.length > 1) {
        gal.classList.add('is-swipeable');
        var drag = null;
        var settling = false;

        function neighborIndex(dir) {
          var next = current + dir;
          if (next < 0 || next >= slides.length) return -1;
          return next;
        }

        function clearDragStyles() {
          for (var i = 0; i < slides.length; i++) {
            slides[i].style.transition = '';
            slides[i].style.transform = '';
            slides[i].style.opacity = '';
            slides[i].style.visibility = '';
          }
          stage.classList.remove('is-dragging');
        }

        function placeSlides(fromIndex, toX, neighbor, neighborX) {
          for (var i = 0; i < slides.length; i++) {
            var s = slides[i];
            if (i === fromIndex) {
              s.style.transform = 'translate3d(' + toX + 'px,0,0)';
              s.style.opacity = '1';
              s.style.visibility = 'visible';
            } else if (i === neighbor) {
              s.style.transform = 'translate3d(' + neighborX + 'px,0,0)';
              s.style.opacity = '1';
              s.style.visibility = 'visible';
            } else {
              s.style.opacity = '0';
              s.style.visibility = 'hidden';
            }
          }
        }

        function commitIndex(index) {
          for (var i = 0; i < slides.length; i++) slides[i].style.transition = 'none';
          show(slides[index].getAttribute('data-oh-gallery-slide'));
          for (var j = 0; j < slides.length; j++) {
            slides[j].style.transform = '';
            slides[j].style.opacity = '';
            slides[j].style.visibility = '';
          }
          void stage.offsetWidth;
          requestAnimationFrame(function () { clearDragStyles(); });
        }

        function glideTo(fromIndex, fromX, neighbor, neighborX, thenIndex) {
          if (reduced) {
            if (thenIndex >= 0) commitIndex(thenIndex);
            else clearDragStyles();
            return;
          }
          var ease = 'transform .26s cubic-bezier(.22,.8,.24,1), opacity .26s ease';
          for (var i = 0; i < slides.length; i++) slides[i].style.transition = ease;
          void stage.offsetWidth;
          placeSlides(fromIndex, fromX, neighbor, neighborX);
          var done = false;
          function finish() {
            if (done) return;
            done = true;
            if (thenIndex >= 0) commitIndex(thenIndex);
            else clearDragStyles();
          }
          var watch = slides[thenIndex >= 0 ? thenIndex : fromIndex];
          if (watch) watch.addEventListener('transitionend', finish, { once: true });
          setTimeout(finish, 320);
        }

        stage.addEventListener('pointerdown', function (e) {
          if (settling) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.target.closest('button, a, input, textarea, select, iframe, model-viewer')) return;
          var img = e.target.closest && e.target.closest('img');
          if (img) img.draggable = false;
          drag = {
            id: e.pointerId,
            x: e.clientX,
            y: e.clientY,
            dx: 0,
            locked: null,
            neighbor: -1,
            lx: e.clientX,
            lt: e.timeStamp,
            vx: 0
          };
        });

        stage.addEventListener('pointermove', function (e) {
          if (!drag || e.pointerId !== drag.id) return;
          var dx = e.clientX - drag.x;
          var dy = e.clientY - drag.y;
          var dt = e.timeStamp - drag.lt;
          if (dt > 0) drag.vx = (e.clientX - drag.lx) / dt;
          drag.lx = e.clientX;
          drag.lt = e.timeStamp;
          if (drag.locked === null) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            if (Math.abs(dy) > Math.abs(dx) * 1.25) { drag = null; return; }
            drag.locked = 'x';
          }
          e.preventDefault();
          var dir = dx < 0 ? 1 : -1;
          var neighbor = neighborIndex(dir);
          drag.dx = dx;
          drag.neighbor = neighbor;
          if (reduced) return;
          if (!drag.captured) {
            drag.captured = true;
            stage.classList.add('is-dragging');
            try { stage.setPointerCapture(e.pointerId); } catch (err) {}
          }
          var width = stage.clientWidth || 1;
          var travel = neighbor < 0 ? dx * 0.35 : dx;
          var incoming = (dx < 0 ? width : -width) + travel;
          for (var i = 0; i < slides.length; i++) slides[i].style.transition = 'none';
          placeSlides(current, travel, neighbor, incoming);
        }, { passive: false });

        function endDrag(e) {
          if (!drag || (e && e.pointerId !== undefined && e.pointerId !== drag.id)) return;
          var info = drag;
          drag = null;
          if (info.locked !== 'x') { clearDragStyles(); return; }
          var width = stage.clientWidth || 1;
          var dir = info.dx < 0 ? 1 : -1;
          if (Math.abs(info.dx) < 24 && Math.abs(info.vx) > 0.35) dir = info.vx < 0 ? 1 : -1;
          var neighbor = neighborIndex(dir);
          var flung = Math.abs(info.vx) > 0.35 && Math.abs(info.dx) > 8;
          var far = Math.abs(info.dx) > Math.max(18, width * 0.1);
          var go = neighbor >= 0 && (far || flung);
          settling = true;
          function done() { settling = false; }
          if (!go) {
            glideTo(current, 0, info.neighbor, info.dx < 0 ? width : -width, -1);
            setTimeout(done, 340);
            return;
          }
          glideTo(neighbor, 0, current, dir > 0 ? -width : width, neighbor);
          setTimeout(done, 340);
        }

        stage.addEventListener('pointerup', endDrag);
        stage.addEventListener('pointercancel', endDrag);
      }

      /* Selecting a pack that has its own featured image swaps the gallery. */
      document.addEventListener('wl:variant:change', function (e) {
        if (e && e.detail && e.detail.mediaId) show(String(e.detail.mediaId));
      });
    });
  }

  /* --------------------------------------------------------------- packs --- */
  /* Variant selector. Each option carries the real Shopify variant id and
     prices in cents, so the form always posts a variant that exists. */
  function initPacks(root) {
    root.querySelectorAll('[data-oh-packs]').forEach(function (group) {
      if (!claim(group, 'Packs')) return;
      /* Price and variant mirrors live all over the page — the sticky buy bar
         and the guarantee CTA sit outside the buy card — so the selection must
         propagate document-wide, not just within the card. */
      var scope = document;
      var options = group.querySelectorAll('[data-oh-pack]');

      function select(opt) {
        options.forEach(function (o) {
          var on = o === opt;
          o.classList.toggle('is-on', on);
          var input = o.querySelector('input[type=radio]');
          if (input) input.checked = on;
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        var id = opt.getAttribute('data-variant-id');
        var price = parseInt(opt.getAttribute('data-price'), 10);
        var compare = parseInt(opt.getAttribute('data-compare'), 10);
        var label = opt.getAttribute('data-pack-label') || '';
        var note = opt.getAttribute('data-pack-note') || '';

        scope.querySelectorAll('[data-oh-variant-input]').forEach(function (i) { i.value = id; });
        /* Single-variant products sell by quantity instead of by pack, so the
           option carries a quantity and the section has pre-multiplied prices. */
        var qty = opt.getAttribute('data-qty');
        if (qty) {
          scope.querySelectorAll('[data-oh-qty-input]').forEach(function (i) { i.value = qty; });
        }
        scope.querySelectorAll('[data-oh-price]').forEach(function (e) { e.textContent = formatMoney(price); });
        scope.querySelectorAll('[data-oh-compare-price]').forEach(function (e) {
          if (compare && compare > price) { e.textContent = formatMoney(compare); e.hidden = false; }
          else { e.hidden = true; }
        });
        scope.querySelectorAll('[data-oh-save]').forEach(function (e) {
          if (compare && compare > price) {
            e.textContent = String(Math.round((1 - price / compare) * 100));
            if (e.parentElement) e.parentElement.hidden = false;
          } else if (e.parentElement) { e.parentElement.hidden = true; }
        });
        scope.querySelectorAll('[data-oh-unit-price]').forEach(function (e) { e.textContent = formatMoney(price / 4); });
        scope.querySelectorAll('[data-oh-pack-label]').forEach(function (e) { e.textContent = label; });
        scope.querySelectorAll('[data-oh-pack-note]').forEach(function (e) { e.textContent = note; });
        document.dispatchEvent(new CustomEvent('wl:variant:change', {
          detail: { variantId: id, mediaId: opt.getAttribute('data-media-id') }
        }));
      }

      options.forEach(function (o) {
        o.addEventListener('click', function () { select(o); });
        o.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(o); }
        });
      });
      var initial = group.querySelector('[data-oh-pack].is-on') || options[0];
      if (initial) select(initial);
    });
  }

  /* ----------------------------------------------------------- countdown --- */
  /* Real same-day-dispatch cutoff, resolved in the VISITOR's timezone (falling
     back to the store's when the section supplies one). Honest by construction:
     it counts to an actual dispatch hour, it is not a fake timer.

     Three things this has to get right:

     1. Timezone. Read from Intl.DateTimeFormat().resolvedOptions().timeZone so
        the clock matches the shopper's wall clock rather than the server's.
     2. DST. Never do wall-clock arithmetic on a UTC instant. We read "now" as
        the visitor's calendar fields, build the cutoff from those fields, and
        measure the gap in those same terms, so the cutoff lands on the local
        hour on both transition days (a 23h and a 25h day).
     3. A 24h ceiling. The displayed clock always counts to the NEXT occurrence
        of the dispatch hour, so it can never exceed 24h. Weekend skipping
        belongs to the delivery DATE, not to the clock -- that distinction is
        what previously let the timer read 38:00:28. */
  function initCountdown(root) {
    root.querySelectorAll('[data-oh-countdown]').forEach(function (el) {
      if (!claim(el, 'Countdown')) return;

      /* The visitor's zone wins; the section's data-oh-tz is the fallback for
         when Intl is unavailable. */
      var tz;
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) { tz = null; }
      if (!tz) tz = el.getAttribute('data-oh-tz') || 'America/New_York';

      var hour = parseInt(el.getAttribute('data-oh-cutoff-hour') || '14', 10);
      if (!(hour >= 0 && hour <= 23)) hour = 14;
      var transit = parseInt(el.getAttribute('data-oh-transit-days') || '4', 10);
      if (!(transit >= 0)) transit = 4;
      /* Calendar days are opt-in per section; business days are the default so
         no order is ever promised a Saturday or Sunday arrival. */
      var useCalendar = el.getAttribute('data-oh-transit-mode') === 'calendar';

      var h = el.querySelector('[data-oh-cd-h]');
      var m = el.querySelector('[data-oh-cd-m]');
      var s = el.querySelector('[data-oh-cd-s]');
      var combo = el.querySelectorAll('[data-oh-cd-combined]');
      /* Scoped to this element, then falling back to the document only when
         this countdown owns no date node of its own -- a document-wide write
         let the drawer's clock overwrite the PDP's delivery date. */
      var dates = el.querySelectorAll('[data-oh-delivery-date]');
      if (!dates.length) dates = document.querySelectorAll('[data-oh-delivery-date]');
      /* Server-rendered sentences: one for before the cutoff, one for after.
         Both ship in the markup so JS-off still reads a real cutoff line. */
      var staticLine = el.querySelector('[data-oh-cd-static], .oh-cart__cutoff-static');
      var todayLine = el.querySelector('[data-oh-cd-today]');
      var tomorrowLine = el.querySelector('[data-oh-cd-tomorrow]');

      function pad(n) { return String(n).padStart(2, '0'); }

      /* The visitor's local calendar fields for a given instant. Parsing
         formatted parts rather than toLocaleString() avoids depending on a
         locale string shape that Date() may or may not parse. */
      var PARTS = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      function fields(at) {
        var o = {};
        PARTS.formatToParts(at).forEach(function (p) {
          if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10);
        });
        /* en-US hour12:false can emit hour 24 for midnight; normalise it. */
        if (o.hour === 24) o.hour = 0;
        return o;
      }

      /* Seconds from local midnight -- the only quantity the clock needs, and
         one that is immune to DST because it never crosses a day boundary. */
      function secondsIntoDay(f) { return f.hour * 3600 + f.minute * 60 + f.second; }

      /* Weekday for a y/m/d triple, via a UTC proxy so no local offset or DST
         shift can move it. Only the weekday is read, never the instant. */
      function dowOf(f) { return new Date(Date.UTC(f.year, f.month - 1, f.day)).getUTCDay(); }

      function addDays(f, n) {
        var d = new Date(Date.UTC(f.year, f.month - 1, f.day));
        d.setUTCDate(d.getUTCDate() + n);
        return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
      }
      function nextBusinessDay(f) {
        var out = addDays(f, 1);
        while (dowOf(out) === 0 || dowOf(out) === 6) out = addDays(out, 1);
        return out;
      }
      function addTransit(f, n) {
        var out = f;
        for (var i = 0; i < n; i++) out = useCalendar ? addDays(out, 1) : nextBusinessDay(out);
        return out;
      }

      var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      var DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      /* Formatted from the y/m/d triple directly, so the printed date is the
         one we computed -- not a re-interpreted instant. */
      function label(f) {
        return DAYS[dowOf(f)] + ', ' + MONTHS[f.month - 1] + ' ' + f.day;
      }

      function tick() {
        var f = fields(new Date());
        var nowSec = secondsIntoDay(f);
        var cutSec = hour * 3600;

        /* Before the cutoff hour, today is still a dispatch candidate. */
        var beforeCutoff = nowSec < cutSec;
        var isWeekend = dowOf(f) === 0 || dowOf(f) === 6;
        var shipsToday = beforeCutoff && !isWeekend;

        /* Always the gap to the NEXT occurrence of the dispatch hour, so the
           clock is bounded by 24h by construction and rolls straight into a
           fresh cycle the second it reaches zero. */
        var remain = beforeCutoff ? (cutSec - nowSec) : (86400 - nowSec + cutSec);
        if (remain >= 86400) remain -= 86400;
        if (remain < 0) remain += 86400;

        var hh = Math.floor(remain / 3600),
            mm = Math.floor((remain % 3600) / 60),
            ss = remain % 60;
        if (h) h.textContent = pad(hh);
        if (m) m.textContent = pad(mm);
        if (s) s.textContent = pad(ss);
        combo.forEach(function (c) { c.textContent = pad(hh) + ':' + pad(mm) + ':' + pad(ss); });

        /* Swap which sentence is visible. The static one is only for JS-off. */
        if (todayLine || tomorrowLine) {
          if (staticLine) staticLine.hidden = true;
          if (todayLine) todayLine.hidden = !shipsToday;
          if (tomorrowLine) tomorrowLine.hidden = shipsToday;
        }

        /* Dispatch day, then transit on top. Recomputed every tick, so the date
           advances the instant the clock rolls over -- no refresh needed. */
        var ship = { year: f.year, month: f.month, day: f.day };
        if (!shipsToday) ship = nextBusinessDay(ship);
        var text = label(addTransit(ship, transit));
        dates.forEach(function (d) { d.textContent = text; });
      }

      tick();

      /* The drawer re-renders its body on every cart change, so a countdown
         inside it is replaced wholesale. Store the interval on the node and
         clear it if the same node is ever re-initialised. */
      if (el.ohCdTimer) clearInterval(el.ohCdTimer);
      el.ohCdTimer = setInterval(tick, 1000);

      /* Background tabs throttle timers, so the clock drifts while hidden.
         Recompute on return. tick() derives everything from the current time
         rather than decrementing a counter, so one call fully resynchronises. */
      if (el.ohCdWake) {
        document.removeEventListener('visibilitychange', el.ohCdWake);
        window.removeEventListener('focus', el.ohCdWake);
      }
      el.ohCdWake = function () { if (!document.hidden) tick(); };
      document.addEventListener('visibilitychange', el.ohCdWake);
      window.addEventListener('focus', el.ohCdWake);

      /* Teardown for callers that remove the node (the drawer, the theme
         editor): drop the interval and the listeners so nothing leaks. */
      el.ohCdDestroy = function () {
        if (el.ohCdTimer) { clearInterval(el.ohCdTimer); el.ohCdTimer = null; }
        if (el.ohCdWake) {
          document.removeEventListener('visibilitychange', el.ohCdWake);
          window.removeEventListener('focus', el.ohCdWake);
          el.ohCdWake = null;
        }
      };
    });
  }

  /* ------------------------------------------------------------- compare --- */
  function initCompare(root) {
    root.querySelectorAll('[data-oh-compare]').forEach(function (el) {
      if (!claim(el, 'Compare')) return;
      var pane = el.querySelector('[data-oh-compare-pane]');
      var handle = el.querySelector('[data-oh-compare-handle]');
      var pct = 50;

      function apply(p) {
        pct = Math.max(0, Math.min(100, p));
        if (pane) pane.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
        if (handle) handle.style.left = pct + '%';
        /* The revealed pane's copy starts at the divider rather than at a fixed
           half, so dragging left never exposes its text on the left of the
           handle. Published as a custom property; the pane's CSS consumes it. */
        el.style.setProperty('--oh-cmp-pos', pct + '%');
        el.setAttribute('aria-valuenow', Math.round(pct));
      }
      function fromEvent(e) {
        var r = el.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
        apply((x / r.width) * 100);
      }
      /* The copy layer sits above the panes, so pointerdown usually lands on a
         descendant rather than the frame. Moves are tracked on the document
         instead of the frame: pointer capture retargets events, and binding
         move/up to the frame alone drops them as soon as the cursor leaves it
         or crosses a child. preventDefault stops the drag turning into a text
         selection, which is what stalls the divider mid-drag. */
      var dragging = false;

      function onMove(e) {
        if (!dragging) return;
        e.preventDefault();
        fromEvent(e);
      }
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', endDrag);
        document.removeEventListener('pointercancel', endDrag);
      }

      el.addEventListener('pointerdown', function (e) {
        /* Ignore secondary buttons so a right-click does not jump the handle. */
        if (e.button && e.button !== 0) return;
        dragging = true;
        e.preventDefault();
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', endDrag);
        document.addEventListener('pointercancel', endDrag);
        fromEvent(e);
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); apply(pct - 4); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); apply(pct + 4); }
        else if (e.key === 'Home') { e.preventDefault(); apply(0); }
        else if (e.key === 'End') { e.preventDefault(); apply(100); }
      });
      apply(50);
    });
  }

  /* ------------------------------------------------------- cart hold --- */
  /* A reservation timer for the cart drawer. Counts down from data-oh-hold
     minutes and persists the deadline in sessionStorage, so reopening the
     drawer resumes the same countdown rather than restarting it. */
  function initCartHold(root) {
    root.querySelectorAll('[data-oh-hold]').forEach(function (el) {
      if (!claim(el, 'CartHold')) return;
      var mins = parseInt(el.getAttribute('data-oh-hold') || '10', 10);
      var out = el.querySelector('[data-oh-hold-time]');
      if (!out) return;

      var KEY = 'ohCartHoldUntil';
      var until = 0;
      try { until = parseInt(sessionStorage.getItem(KEY) || '0', 10); } catch (e) {}
      if (!until || until < Date.now()) {
        until = Date.now() + mins * 60000;
        try { sessionStorage.setItem(KEY, String(until)); } catch (e) {}
      }

      function pad(n) { return String(n).padStart(2, '0'); }
      function tick() {
        var left = Math.max(0, Math.floor((until - Date.now()) / 1000));
        out.textContent = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
        if (left <= 0) {
          clearInterval(id);
          /* Expired holds restart on the next open rather than stranding a
             cart at 00:00. */
          try { sessionStorage.removeItem(KEY); } catch (e) {}
        }
      }
      tick();
      var id = setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------------- ring --- */
  function initRings(root) {
    root.querySelectorAll('[data-oh-ring]').forEach(function (el) {
      if (!claim(el, 'Ring')) return;
      var target = parseFloat(el.getAttribute('data-oh-ring')) || 0;
      var out = el.querySelector('[data-oh-ring-value]');
      function paint(v) {
        el.style.setProperty('--oh-ring-p', v);
        if (out) out.textContent = Math.round(v) + '%';
      }
      if (reduced) { paint(target); return; }
      paint(0);
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        var t0 = performance.now();
        (function tick(ts) {
          var p = Math.min((ts - t0) / 1400, 1);
          paint(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
          else paint(target);
        })(t0);
      }, { threshold: .4 }).observe(el);
    });
  }

  /* ------------------------------------------------------------ validate --- */
  /* Inline form validation. The browser's own validation is left on as the
     fallback (no novalidate until we know JS is running), so a failed script
     degrades to native messages rather than to no validation at all. */
  function initValidate(root) {
    root.querySelectorAll('[data-oh-validate]').forEach(function (form) {
      if (!claim(form, 'Validate')) return;
      form.setAttribute('novalidate', 'novalidate');
      var fields = form.querySelectorAll('[data-oh-field]');

      function check(field) {
        var input = field.querySelector('input, textarea, select');
        if (!input) return true;
        var rule = field.getAttribute('data-oh-field') || '';
        var value = (input.value || '').trim();
        var ok = true;
        if (rule.indexOf('required') > -1 && !value) ok = false;
        if (ok && rule.indexOf('email') > -1 && value && !/.+@.+\..+/.test(value)) ok = false;
        var min = rule.match(/min:(\d+)/);
        if (ok && min && value.length < parseInt(min[1], 10)) ok = false;
        field.classList.toggle('is-bad', !ok);
        var err = field.querySelector('[data-oh-error]');
        if (err) err.hidden = ok;
        input.setAttribute('aria-invalid', ok ? 'false' : 'true');
        return ok;
      }

      fields.forEach(function (f) {
        var input = f.querySelector('input, textarea, select');
        if (!input) return;
        input.addEventListener('blur', function () { check(f); });
        input.addEventListener('input', function () {
          if (f.classList.contains('is-bad')) check(f);
        });
      });

      form.addEventListener('submit', function (e) {
        var firstBad = null;
        fields.forEach(function (f) {
          if (!check(f) && !firstBad) firstBad = f;
        });
        if (firstBad) {
          e.preventDefault();
          var input = firstBad.querySelector('input, textarea, select');
          if (input) input.focus();
        }
      });
    });
  }

  /* -------------------------------------------------------------- dialog --- */
  /* A <dialog> opened from a link that already points at the thing it shows.
     With JS off the link is the feature: it opens the image on its own page.
     Here the click is intercepted instead and showModal() takes over, which
     brings ESC, the focus trap and the backdrop with it for free. */
  function initDialog(root) {
    root.querySelectorAll('[data-oh-dialog]').forEach(function (dialog) {
      if (!claim(dialog, 'Dialog')) return;
      if (!dialog.showModal) return; /* no native support: the link stands */
      var id = dialog.getAttribute('data-oh-dialog');
      var opener = null;

      function open(e) {
        if (e) { e.preventDefault(); opener = e.currentTarget; }
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
      function close() {
        if (dialog.open) dialog.close();
      }

      dialog.addEventListener('close', function () {
        document.body.style.overflow = '';
        if (opener && opener.focus) opener.focus();
      });
      /* showModal() puts the backdrop behind the dialog element itself, so a
         click that lands on the element and not on its panel is a backdrop
         click. */
      dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
      dialog.querySelectorAll('[data-oh-dialog-close]').forEach(function (b) {
        b.addEventListener('click', close);
      });
      document.querySelectorAll('[data-oh-dialog-open="' + id + '"]').forEach(function (b) {
        b.addEventListener('click', open);
      });
    });
  }

  /* --------------------------------------------------------------- init --- */
  /* Each behaviour is isolated: a section that is absent, or markup that is
     malformed, must not stop the rest of the page from binding. */
  var BEHAVIOURS = [
    ['reveal', initReveal], ['marquee', initMarquee], ['countUp', initCountUp],
    ['accordion', initAccordion], ['rail', initRail], ['steps', initSteps],
    ['words', initWords], ['magnetic', initMagnetic], ['parallax', initParallax],
    ['nav', initNav], ['lab', initLab], ['drawer', initDrawer],
    ['switch', initSwitch], ['hotspots', initHotspots], ['packs', initPacks],
    ['gallery', initGallery],
    ['countdown', initCountdown], ['compare', initCompare], ['rings', initRings],
    ['ugcVideo', initUgcVideo], ['cartHold', initCartHold],
    ['validate', initValidate], ['dialog', initDialog]
  ];

  function initAll(root) {
    root = root || document;
    for (var i = 0; i < BEHAVIOURS.length; i++) {
      try {
        BEHAVIOURS[i][1](root);
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('Oakhold: ' + BEHAVIOURS[i][0] + ' failed to initialise', e);
        }
      }
    }
  }

  window.Oakhold = window.Oakhold || {};
  window.Oakhold.init = initAll;
  window.Oakhold.reduced = reduced;
  window.Oakhold.formatMoney = formatMoney;
  window.Oakhold.openDrawer = function (id) {
    var d = document.querySelector('[data-oh-drawer="' + id + '"]');
    if (d && d.wlOpen) d.wlOpen();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: re-bind whatever the editor swapped in */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
